import {
    Row,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import Download from '~/icons/iconography/download.svg';
import { ShopTypes, UmbracoTypes } from '~/lib/data-contract';
import { usePage, useTranslations } from '~/services/umbraco/hooks';
import { Button, LinkButton } from '~/shared/components/Button';
import { Text } from '~/shared/components/Text';
import { usePaginationState } from '~/state/useOrderHistoryState';
import { Table as OrderHistoryTable } from '~/templates/blocks/shared/Table/Table';
import getInternalInvoiceUrl from '../../M202AccountInformation/helper/getInvoiceUrl';
import { DownloadDocuments, StyledTable, StyledTableHeader, TableCellLink } from '../styles';
import Checkbox from './Checkbox';
import DeliveryMethod from './DeliveryMethod';
import DeliveryStatus from './DeliveryStatus';
import { useUserProfile } from '~/services/shop/hooks/mit-fog';

type OrderHistoryTableProps = {
    data: ShopTypes.OrderHeader[];
};

const getBuildBaseURL = (
    customerId: string | undefined,
    selectedOrders: (string | undefined)[],
) => {
    return `https://kvalitet.johannesfog.dk/QualityBundle/Index?cust=${customerId}&orders=${selectedOrders.join(
        ';',
    )}`;
};

const columnHelper = createColumnHelper<ShopTypes.OrderHeader>();

const select = [10, 50, 100];

export function Table({ data }: OrderHistoryTableProps) {
    const { data: profile } = useUserProfile();
    const { translate } = useTranslations();

    const [isRowSelected, setIsRowSelected] = useState(false);

    const { data: page } = usePage<UmbracoTypes.IP200MitFogPage>();
    const url = page?.pageElements?.map((pageElement) => pageElement.detailsReference.url);

    const { pageIndex, pageSize, setPagination } = usePaginationState();

    const columns = useMemo(
        () => [
            columnHelper.accessor('orderDate', {
                header: translate('mit-fog.orderHistory.orderDate'),
                cell: (info) => {
                    const value = info.getValue();
                    return (
                        <Text>
                            {value === undefined
                                ? ''
                                : new Date(value).toLocaleDateString('en-GB').replace(/\//g, '-')}
                        </Text>
                    );
                },
            }),
            columnHelper.accessor('orderId', {
                header: translate('mit-fog.orderHistory.orderId'),
                cell: (info) => {
                    const orderIdUrl = new URLSearchParams();
                    orderIdUrl.set('q', info.getValue() ?? '');
                    const value = info.getValue();
                    return (
                        <>
                            {value && (
                                <TableCellLink href={`${url}?${orderIdUrl.toString()}`} withIcon>
                                    {info.getValue()}
                                </TableCellLink>
                            )}
                        </>
                    );
                },
            }),
            columnHelper.accessor('status', {
                header: translate('mit-fog.orderDetails.status'),
                cell: (info) => (
                    <DeliveryStatus
                        status={info.getValue()}
                        statusCode={info.row.original?.statusCode}
                    />
                ),
            }),
            columnHelper.accessor('deliveryAddress.addressLine1', {
                header: translate('mit-fog.orderDetails.deliveryAddress'),
                cell: (info) => <Text>{info.getValue()?.toLocaleUpperCase()}</Text>,
                sortingFn: (rowA: any, rowB: any): number => {
                    const a = rowA.original.deliveryAddress.addressLine1;
                    const b = rowB.original.deliveryAddress.addressLine1;

                    if (a === null && b === null) return 0;
                    if (a === null) return 1;
                    if (b === null) return -1;

                    if (a === undefined && b === undefined) return 0;
                    if (a === undefined) return 1;
                    if (b === undefined) return -1;

                    const stringA = String(a);
                    const stringB = String(b);

                    return stringA.localeCompare(stringB);
                },
            }),
            columnHelper.accessor('customersReference', {
                header: translate('mit-fog.orderHistory.customersReference'),
                cell: (info) => <Text>{info.getValue()?.toLocaleUpperCase()}</Text>,
            }),
            columnHelper.accessor('deliveryMethod', {
                header: translate('mit-fog.orderHistory.deliveryMethod'),
                cell: (info) => <DeliveryMethod method={info.getValue()} />,
            }),
            {
                id: 'link',
                header: translate('mit-fog.download'),
                cell: ({ row }: { row: Row<ShopTypes.OrderHeader> }) => {
                    const rowData = row.original;
                    const url = getInternalInvoiceUrl(rowData.customerId, rowData.orderId, null);

                    if (!url || !rowData.hasInvoice) return null;

                    const handleClick = (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (url) {
                            window.location.href = url;
                        } else {
                            console.error('Invalid customer ID or order number');
                        }
                    };

                    return (
                        <TableCellLink
                            customIcon={<Download />}
                            withIcon
                            href={url}
                            onClick={handleClick}
                        >
                            {translate('mit-fog.invoice')}
                        </TableCellLink>
                    );
                },
                meta: {
                    isText: true,
                },
            },
            {
                id: 'checkbox',
                header: () => (
                    <StyledTableHeader
                        className="my-specific-class"
                        onClick={table.getToggleAllPageRowsSelectedHandler()}
                    >
                        {isRowSelected
                            ? translate('mit-fog.orderHistory.removeAll')
                            : translate('mit-fog.orderHistory.selectAll')}
                    </StyledTableHeader>
                ),
                maxSize: 100,
                cell: ({ row }: { row: Row<ShopTypes.OrderHeader> }) => (
                    <Checkbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                    />
                ),
                meta: {
                    align: 'left',
                    isText: true,
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [translate, url],
    );

    const table = useReactTable({
        data,
        columns,
        enableRowSelection: true,
        initialState: {
            sorting: [
                {
                    id: 'orderDate',
                    desc: true,
                },
            ],
        },
        state: {
            pagination: {
                pageIndex,
                pageSize,
            },
        },
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const firstValue = table.getSelectedRowModel().flatRows.map((row) => row.original.orderId);

    const areAllPageRowsSelected = table.getIsAllPageRowsSelected();

    useEffect(() => {
        if (
            table.getPaginationRowModel().flatRows.length ===
            table.getSelectedRowModel().flatRows.length
        ) {
            setIsRowSelected(true);
        } else {
            setIsRowSelected(false);
        }
    }, [areAllPageRowsSelected, table]);

    return (
        <>
            <DownloadDocuments>
                {firstValue.length ? (
                    <LinkButton
                        href={getBuildBaseURL(profile?.user?.currentCustomerId, firstValue)}
                        target="_blank"
                        color="#013d7a"
                        variant="fill"
                        fullWidth
                        customIcon={<Download />}
                    >
                        {translate('mit-fog.orders.downloadDocuments')}
                    </LinkButton>
                ) : (
                    <Button variant="fill" fullWidth customIcon={<Download />} disabled>
                        {translate('mit-fog.orders.downloadDocuments')}
                    </Button>
                )}
            </DownloadDocuments>
            <StyledTable>
                {/* @ts-ignore */}
                <OrderHistoryTable data={data} table={table} select={select} />
            </StyledTable>
        </>
    );
}
